"use client";

import { useEffect, useState } from "react";
import { Info, Save, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { AgentMarkdownEditor } from "@/components/agent-markdown-editor";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  banner?: string;
  optional?: boolean;
  value: string;
  max: number;
  emptyHint?: string;
  defaultValue?: string;
  onSave: (next: string) => void;
};

export function AgentPromptEditDialog({
  open,
  onOpenChange,
  title,
  description,
  banner,
  optional,
  value,
  max,
  emptyHint,
  defaultValue,
  onSave,
}: Props) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="agent-prompt-dialog flex flex-col bg-white p-0 gap-0 overflow-hidden border-0 max-h-[min(88vh,820px)]"
        aria-describedby={undefined}
      >
        <header className="agent-prompt-dialog-header">
          <div className="agent-prompt-dialog-titles">
            <div className="agent-prompt-dialog-title-row">
              <DialogTitle className="agent-prompt-dialog-title">{title}</DialogTitle>
              {optional ? <span className="agent-prompt-optional">Optional</span> : null}
            </div>
            <DialogDescription className="agent-prompt-dialog-desc">{description}</DialogDescription>
          </div>
          <button
            type="button"
            className="agent-edit-close"
            aria-label="Close"
            onClick={() => onOpenChange(false)}
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </header>

        {banner ? (
          <div className="agent-prompt-dialog-banner" role="note">
            <Info size={14} aria-hidden />
            <span>{banner}</span>
          </div>
        ) : null}

        <div className="agent-prompt-dialog-body">
          <AgentMarkdownEditor
            value={draft}
            onChange={setDraft}
            max={max}
            label="Prompt"
            emptyHint={emptyHint}
            areaClassName="agent-conv-textarea--dialog"
          />
          {defaultValue != null && (
            <button
              type="button"
              className="agent-prompt-reset"
              onClick={() => setDraft(defaultValue)}
            >
              Reset to default
            </button>
          )}
        </div>

        <footer className="agent-prompt-dialog-footer">
          <button type="button" className="agent-edit-btn" onClick={() => onOpenChange(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="agent-edit-btn agent-edit-btn--primary"
            onClick={() => {
              onSave(draft);
              onOpenChange(false);
            }}
          >
            <Save size={15} strokeWidth={2} aria-hidden />
            Save prompt
          </button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}
